import { BRAND, TRIAL_DAYS, type Account, t } from '@procumi/shared';
import { useState } from 'react';
import { api, errMsg } from '../lib/api';
import { rich } from '../lib/rich';

/**
 * Intrarea în cont. „Creează cont” duce la înscrierea deschisă (SignupScreen).
 */
type Mode = 'login' | 'forgot';

export function AuthScreen({
  onDone,
  onSignup,
}: {
  onDone: (account: Account) => void;
  onSignup: () => void;
}) {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Emailul pe care s-a cerut linkul de parolă nouă, după ce s-a cerut. */
  const [sentTo, setSentTo] = useState<string | null>(null);

  const switchTo = (m: Mode) => {
    setMode(m);
    setError(null);
    setSentTo(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'forgot') {
        await api.forgotPassword(email);
        setSentTo(email.trim());
        return;
      }
      onDone(await api.login({ email, password }));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-brand">
          <img className="logo" src="/brand/procumi-lockup.svg" alt={BRAND.name} />
          <small>{BRAND.tagline}</small>
        </div>

        <div className="sheet pad" style={{ display: 'grid', gap: 14 }}>
          {mode === 'forgot' ? (
            <div>
              <h2>{t('Ai uitat parola?')}</h2>
              <p className="hint" style={{ marginTop: 6 }}>
                {t('Scrie emailul cu care intri. Îți trimitem un link din care îți alegi o parolă nouă.')}
              </p>
            </div>
          ) : (
            <h2>{t('Intră în cont')}</h2>
          )}

          {sentTo ? (
            // Același mesaj și pentru un email fără cont: formularul nu spune
            // cine e înscris.
            <div className="auth-ok" role="status">
              {rich(
                'Dacă există un cont pe <b>{0}</b>, îți vine un mail cu linkul pentru parola nouă. Linkul merge o oră. Dacă nu-l vezi în câteva minute, uită-te și în Spam.',
                sentTo,
              )}
            </div>
          ) : (
            <label className="f" htmlFor="auth-email">
              {t('Email')}
              <input
                id="auth-email"
                className="inp"
                type="email"
                value={email}
                autoComplete="username"
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
          )}

          {mode !== 'forgot' ? (
            <label className="f" htmlFor="auth-password">
              {t('Parolă')}
              <input
                id="auth-password"
                className="inp"
                type="password"
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
          ) : null}

          {error ? (
            <div className="auth-err" role="alert">
              {error}
            </div>
          ) : null}

          {mode === 'forgot' ? (
            <>
              {sentTo ? null : (
                <button className="btn primary" type="submit" disabled={busy}>
                  {busy ? t('Se trimite…') : t('Trimite-mi linkul')}
                </button>
              )}
              <button className="btn" type="button" onClick={() => switchTo('login')}>
                {t('Înapoi la intrare')}
              </button>
            </>
          ) : (
            <>
              <button className="btn primary" type="submit" disabled={busy}>
                {t('Intră în cont')}
              </button>
              <button className="auth-link" type="button" onClick={() => switchTo('forgot')}>
                {t('Am uitat parola')}
              </button>
            </>
          )}
        </div>

        {mode === 'login' ? (
          <div className="auth-new">
            <span>{t('Firma ta nu are cont?')}</span>
            <button className="btn" type="button" onClick={onSignup}>
              {t('Încearcă gratuit {0} zile', TRIAL_DAYS)}
            </button>
            <small className="hint" style={{ margin: 0 }}>
              {t('Fără card. Colegii intră cu invitația primită din firmă.')}
            </small>
          </div>
        ) : null}
      </form>
    </div>
  );
}
